import React, { useState, useEffect, useCallback, useRef } from "react";
import { getTodaysPrograms, KioskProgram, KioskProgramsResponse } from "../../api/kioskAPI";
import { KioskSettings } from "./KioskMode";
import "./KioskProgramCards.css";
import { localTodayYmd } from "@mediatechgroup/calendar-date";

interface KioskProgramCardsProps {
  onSelectProgram: (program: KioskProgram) => void;
  onLogout: () => void;
  settings: KioskSettings;
  /**
   * Where the classes come from. Self check-in uses today's list (the
   * default); staff kiosk mode passes its today/yesterday listing.
   */
  loadPrograms?: () => Promise<KioskProgramsResponse>;
  title?: string;
  /** Rendered under the title (staff mode's day switch). */
  headerExtra?: React.ReactNode;
  emptyText?: string;
}

const KioskProgramCards: React.FC<KioskProgramCardsProps> = ({
  onSelectProgram,
  onLogout,
  settings,
  loadPrograms: fetchPrograms = getTodaysPrograms,
  title = "Select Your Program",
  headerExtra,
  emptyText = "No programs scheduled for today",
}) => {
  const [programs, setPrograms] = useState<KioskProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ date: string; dayOfWeek: string } | null>(null);
  const [timeParts, setTimeParts] = useState<{ hour: string; minute: string; ampm: string }>({ hour: "", minute: "", ampm: "" });
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);
  const clockRef = useRef<HTMLButtonElement>(null);
  const currentDateRef = useRef<string>("");

  // Helper to get current date in tenant's timezone
  const getCurrentDateInTimezone = useCallback((timezone: string): string => {
    try {
      const now = new Date();
      return now.toLocaleDateString("en-CA", { timeZone: timezone }); // en-CA gives YYYY-MM-DD format
    } catch {
      return localTodayYmd();
    }
  }, []);

  const loadPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchPrograms();
      if (response.success) {
        setPrograms(response.data);
        setMeta({ date: response.meta.date, dayOfWeek: response.meta.dayOfWeek });
        // Update the date ref so we can detect when it changes
        currentDateRef.current = getCurrentDateInTimezone(settings.timezone);
      } else {
        setError("Failed to load programs");
      }
    } catch (err) {
      setError("Unable to connect. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [getCurrentDateInTimezone, settings.timezone, fetchPrograms]);

  // Load programs on mount
  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  // Realtime clock with timezone support and date change detection
  useEffect(() => {
    const updateClock = () => {
      try {
        const now = new Date();
        const hour = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          hour: "numeric",
          hour12: true,
        }).replace(/\s*(AM|PM)$/i, "");
        const minute = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          minute: "2-digit",
        });
        const ampm = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          hour: "numeric",
          hour12: true,
        }).match(/(AM|PM)$/i)?.[0] || "";
        setTimeParts({ hour, minute, ampm });

        // Check for date change (midnight rollover)
        const currentDate = getCurrentDateInTimezone(settings.timezone);
        if (currentDateRef.current && currentDateRef.current !== currentDate) {
          // Date has changed, reload programs
          currentDateRef.current = currentDate;
          loadPrograms();
        }
      } catch {
        const now = new Date();
        const hour = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        }).replace(/\s*(AM|PM)$/i, "");
        const minute = now.toLocaleTimeString("en-US", {
          minute: "2-digit",
        });
        const ampm = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        }).match(/(AM|PM)$/i)?.[0] || "";
        setTimeParts({ hour, minute, ampm });
      }
    };

    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [settings.timezone, getCurrentDateInTimezone, loadPrograms]);

  // Close logout menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clockRef.current && !clockRef.current.contains(e.target as Node)) {
        setShowLogoutMenu(false);
      }
    };

    if (showLogoutMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLogoutMenu]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="kiosk-screen kiosk-programs">
        <div className="kiosk-loading">
          <div className="kiosk-loading__spinner" />
          <p>Loading programs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kiosk-screen kiosk-programs">
        <div className="kiosk-empty">
          <div className="kiosk-empty__icon">&#9888;</div>
          <p>{error}</p>
          <button className="kiosk-retry-btn" onClick={loadPrograms}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kiosk-screen kiosk-programs">
      <div className="kiosk-header">
        <div className="kiosk-header__logo">
          {settings.logoUrl && (
            <img
              src={settings.logoUrl}
              alt="Logo"
              className="kiosk-header__logo-img"
            />
          )}
        </div>
        <div className="kiosk-header__title">
          <h1>{title}</h1>
          {meta && (
            <p>
              {meta.dayOfWeek}, {formatDate(meta.date)}
            </p>
          )}
          {headerExtra}
        </div>
        <div className="kiosk-header__clock-wrapper">
          <button
            ref={clockRef}
            className="kiosk-header__clock"
            onClick={() => setShowLogoutMenu(!showLogoutMenu)}
          >
            {timeParts.hour}<span className="kiosk-clock__colon">:</span>{timeParts.minute} {timeParts.ampm}
          </button>
          {showLogoutMenu && (
            <div className="kiosk-header__logout-menu">
              <button
                className="kiosk-header__logout-btn"
                onClick={onLogout}
              >
                Staff Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {programs.length === 0 ? (
        <div className="kiosk-empty">
          <div className="kiosk-empty__icon">&#128197;</div>
          <p>{emptyText}</p>
        </div>
      ) : (
        <div className="kiosk-cards kiosk-program-cards">
          {programs.map((program) => (
            <button
              key={program.id}
              className="kiosk-program-card"
              onClick={() => onSelectProgram(program)}
            >
              {program.imageUrl ? (
                <div
                  className="kiosk-program-card__image"
                  style={{ backgroundImage: `url(${program.imageUrl})` }}
                />
              ) : (
                <div className="kiosk-program-card__image kiosk-program-card__image--placeholder">
                  <span>{program.label.charAt(0)}</span>
                </div>
              )}
              <div className="kiosk-program-card__content">
                <h2 className="kiosk-program-card__title">{program.label}</h2>
                {program.description && (
                  <p className="kiosk-program-card__description">
                    {program.description}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default KioskProgramCards;
